import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { MarkdownEditor, MarkdownEditorApi } from "@/text/MarkdownEditor";
import { classNames } from "@/utils/classNames";
import { isEmpty } from "lodash";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { CSSTransitionProps } from "react-transition-group/CSSTransition";
import { Box, VBox } from "./Box";
import { boxClassNames } from "./boxHelpers";
import { Button, ButtonProps, IconButton } from "./Button";
import { DropContainer } from "./DropContainer";
import { Icon } from "./Icon";
import { IconName } from "./iconMapping";
import { LoadingIndicator } from "./LoadingIndicator";
import { SelectFilesButton, SelectFilesButtonProps } from "./SelectFilesButton";
import { useElementId } from "@/utils/reactHelpers";
import { BgColor } from "./colors";

export type AiPromptSource = {
  id: string;
  icon?: IconName;
  label: string | TMessage;
  loading?: boolean;
  status?: string | TMessage;
  bg?: BgColor;
  removable?: boolean;
};

export type AiPromptProps = {
  autofocus?: boolean;
  disabled?: boolean;
  fileUploadConfig: FileUploadConfig;
  markdown?: boolean;
  onPromptChange: (prompt: string) => void;
  onSourceRemove?: (source: AiPromptSource) => void;
  onSubmit?: () => void;
  onUploadFiles?: SelectFilesButtonProps["onChange"];
  prompt: string;
  promptInputCtrlRef?: (ctrl: { focus: () => void } | null) => void;
  promptInputId?: string;
  promptLabel?: string | TMessage;
  promptPlaceholder?: string | TMessage;
  sources?: AiPromptSource[];
  submitButtonProps?: Partial<ButtonProps>;
  submitLabel?: ButtonProps["label"];
  submitDisabled?: boolean;
  submitLoading?: boolean;
};

const AiPromptSourceChip = observer(
  ({
    source: s,
    onRemove,
    ...props
  }: { source: AiPromptSource; onRemove?: () => void } & Partial<
    CSSTransitionProps<HTMLDivElement>
  >) => {
    const { tf } = useTranslation();
    const ref = useRef<HTMLDivElement | null>(null);

    return (
      <CSSTransition
        {...props}
        classNames={{
          appear: "transition-all duration-200 opacity-0",
          appearActive: "transition-all duration-200 opacity-100",
          enter: "transition-all duration-200 opacity-0",
          enterActive: "transition-all duration-200 opacity-100",
          exitActive: "transition-all duration-150 opacity-0",
        }}
        nodeRef={ref}
        timeout={{
          appear: 200,
          enter: 200,
          exit: 150,
        }}
      >
        <Box
          ref={ref}
          alignItems="center"
          border
          rounded
          paddingLeft={"md"}
          padding={"sm"}
          className="min-w-0"
          textSize="sm"
          bg={s.bg}
        >
          {s.loading ? <LoadingIndicator size="sm" /> : s.icon ? <Icon name={s.icon} /> : undefined}
          <VBox gap={false}>
            <span className={s.loading ? "-mt-1.5" : undefined}>{tf(s.label)}</span>
            {s.status && (
              <div className="relative h-3 -mt-1.5">
                <span className="absolute w-full truncate text-xs subtle">{tf(s.status)}</span>
              </div>
            )}
          </VBox>

          <div className="relative"></div>
          {onRemove && (
            <IconButton
              className="-m-3"
              color="gray"
              icon={"x"}
              tooltip={{ key: "remove" }}
              onClick={onRemove}
            />
          )}
        </Box>
      </CSSTransition>
    );
  },
);

export const AiPrompt = observer(
  ({
    autofocus,
    disabled,
    fileUploadConfig,
    markdown,
    onPromptChange,
    onSourceRemove,
    onSubmit,
    onUploadFiles,
    prompt,
    promptInputCtrlRef,
    promptLabel,
    promptPlaceholder = { key: "ai-prompt.placeholder" },
    sources,
    submitButtonProps,
    submitDisabled,
    submitLabel,
    submitLoading,
    ...props
  }: AiPromptProps) => {
    const { tf } = useTranslation();
    const [markdownEditorRef, setMarkdownEditorRef] = useState<MarkdownEditorApi | null>(null);
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
    const promptInputId = useElementId(props.promptInputId);

    useEffect(() => {
      promptInputCtrlRef?.({
        focus: () => {
          inputRef?.focus();
          inputRef?.select();
          markdownEditorRef?.focus();
        },
      });
      return () => {
        promptInputCtrlRef?.(null);
      };
      // eslint-disable-next-line
    }, [inputRef, markdownEditorRef]);

    return (
      <DropContainer
        onDrop={onUploadFiles}
        fileUploadConfig={fileUploadConfig}
        disabled={!onUploadFiles}
      >
        {({ active }) => (
          <VBox
            gap={"md"}
            className={classNames(
              "p-md",
              "bg-default",
              active
                ? [
                    "border-2", //
                    "rounded-sm",
                    "border-dashed",
                    "border-primary",
                  ]
                : [
                    "border",
                    "rounded-sm",
                    "border-gray",
                    "hover:border-gray-hard",
                    "hover:focus-within:border-primary",
                    "focus-within:border-primary",
                    "focus-within:ring-1",
                  ],
            )}
            htmlProps={{
              onClick: (event) => {
                inputRef?.focus();
                if (event.target !== inputRef) {
                  inputRef?.select();
                }
                markdownEditorRef?.focus();
              },
            }}
          >
            <Box alignItems="end">
              <VBox grow gap={false}>
                {promptLabel && (
                  <label htmlFor={promptInputId} className="text-sm subtle">
                    {tf(promptLabel)}
                  </label>
                )}
                {markdown ? (
                  <MarkdownEditor
                    labelId={promptInputId}
                    autofocus={autofocus}
                    editorRef={setMarkdownEditorRef}
                    value={prompt}
                    onChange={onPromptChange}
                    withoutToolbar
                    disableMinHeight
                    undecorated
                    placeholder={tf(promptPlaceholder)}
                    readOnly={disabled}
                  />
                ) : (
                  <input
                    ref={setInputRef}
                    id={promptInputId}
                    className="outline-hidden"
                    type={"text"}
                    value={prompt}
                    placeholder={tf(promptPlaceholder)}
                    autoFocus={autofocus}
                    onChange={(event) => onPromptChange(event.target.value)}
                    disabled={disabled}
                  />
                )}
              </VBox>
              {onSubmit && (
                <Button
                  variant={"primary"}
                  label={submitLabel}
                  disabled={disabled || submitDisabled}
                  loading={submitLoading}
                  onClick={onSubmit}
                  {...submitButtonProps}
                />
              )}
            </Box>
            {!isEmpty(sources) && (
              <Box wrap>
                <TransitionGroup className={boxClassNames({ wrap: true })}>
                  {sources?.map((s) => (
                    <AiPromptSourceChip
                      key={s.id}
                      source={s}
                      onRemove={
                        onSourceRemove && s.removable !== false
                          ? () => onSourceRemove(s)
                          : undefined
                      }
                    />
                  ))}
                  {onUploadFiles && (
                    <SelectFilesButton
                      variant="secondary"
                      icon="plus"
                      size="sm"
                      label={false}
                      tooltip={{ key: "ai-prompt.attach.tooltip" }}
                      fileUploadConfig={fileUploadConfig}
                      onChange={onUploadFiles}
                    />
                  )}
                </TransitionGroup>
              </Box>
            )}
          </VBox>
        )}
      </DropContainer>
    );
  },
);
